import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { DomSanitizer } from "@angular/platform-browser";
import { Select } from "@ngxs/store";
import { BehaviorSubject, combineLatest, Observable, Subject } from "rxjs";
import { filter, first, mergeMap, takeUntil, tap } from "rxjs/operators";
import { AppState } from "src/app/app.state";
import {
  MultiSelectChipOption,
  MultiSelectChipsComponent,
} from "src/app/components/multi-select-chips/multi-select-chips.component";
import { ZonesSelectorComponent } from "src/app/components/zones-selector/zones-selector.component";
import { InspectionTypeSummary } from "src/app/models/inspection-type-summary.model";
import { LanguageDictionary } from "src/app/models/language-dictionary.model";
import {
  DisplayedCategory,
  DisplayedZone,
  EXCLUDED_ZONES,
  MirRule,
  MirRuleCreateRequest,
  RuleZone,
  TripStartOrEnd,
  WorkPeriod,
} from "src/app/models/mir-rule.model";
import { LanguageDictionaryService } from "src/app/services/language-dictionary/language-dictionary.service";
import { RulesService } from "src/app/services/rules/rules.service";
import { ZonesService } from "src/app/services/zones.service";
import { isDefined } from "src/utils/isDefined/isDefined";
import { WizardId, WizardService } from "../../../services/wizard/wizard.service";
import { ValidatedStep } from "../../config/validated-step";
import { makeKey, TripSemantics } from "../define-duty/define-duty.component";
import { StepProps } from "../step-props";

interface StepWithElements {
	stepLabel: string;
	validatedElements: {
		label: string;
		value: string;
		error?: string | null;
		extraHtml?: (sanitizer: DomSanitizer) => unknown;
	}[];
}

interface ZoneOption extends MultiSelectChipOption {
	categoryId?: string;
}

@Component({
	selector: "app-review-and-submit",
	templateUrl: "./review-and-submit.component.html",
	styleUrls: ["./review-and-submit.component.scss"],
})
export class ReviewAndSubmitComponent implements OnInit, OnDestroy, AfterViewInit {
	@Input() getStepValidators: () => ValidatedStep[];
	@Input() getPayloads: () => ((wizardService: WizardService) => Record<string, unknown>)[];

	@Select(AppState.getSelectedCompanyId) selectedCompanyId$: Observable<string>;
	@Select(AppState.selectLanguageDictionary) languageDictionary$: Observable<LanguageDictionary>;
	@Select(AppState.selectInspectionTypes) inspectionTypes$: Observable<InspectionTypeSummary[]>;

	@ViewChild("chips") chips: MultiSelectChipsComponent;

	EXCLUDED_ZONES = EXCLUDED_ZONES;
	stepValidators: ValidatedStep[] = [];
	stepsWithElements: StepWithElements[] = [];

	form!: FormGroup;
	existingRuleNames: string[] = [];
	showNameExistsError = false;
	excludedZones: RuleZone[] = [];
	zones: ZoneOption[] = [];

	private onDestroy$ = new Subject<void>();
	wizardId = WizardId.createRule;

	zonesCount$ = this.zonesService.zoneCount$;

	startAndEndZones$: BehaviorSubject<{ start: RuleZone[]; end: RuleZone[] }> = new BehaviorSubject(null);

	startAndEndInspectionTypes$: BehaviorSubject<{ start: MultiSelectChipOption[]; end: MultiSelectChipOption[] }> =
		new BehaviorSubject(null);

	mergedTripBlocks: { tripStart: TripStartOrEnd; tripEnd: TripStartOrEnd } = { tripStart: null, tripEnd: null };

	catMap: Map<string, DisplayedCategory[]>;

	title = "Select zones to exclude";

	hasDupeZones = false;
	divisionId: any;

	constructor(
		public wizardService: WizardService,
		public sanitizer: DomSanitizer,
		private fb: FormBuilder,
		private rulesService: RulesService,
		private zonesService: ZonesService,
		private langService: LanguageDictionaryService,
		private dialog: MatDialog,
		private ref: ElementRef,
	) {}

	ngAfterViewInit() {
		// Focus the rule name input when the component loads
		const ruleNameInput = (this.ref.nativeElement as HTMLElement).querySelector<HTMLInputElement>(
			'input[formControlName="ruleName"]',
		);
		ruleNameInput?.focus();
	}

	setInspectionsSummary(stepValidators: StepWithElements[]) {
		combineLatest([this.rulesService.ruleToPatch$.pipe(filter(isDefined)), this.inspectionTypes$])
			.pipe(first())
			.subscribe(([rule, inspectionTypes]) => {
				const INSP_TYPES = "Inspection Types";
				const [start, end] = stepValidators.map(step => step.validatedElements);

				const startInspections = start?.find(el => el?.label === INSP_TYPES);

				const endInspections = end?.find(el => el?.label === INSP_TYPES);

				const summarize = (insps: { inspectionType: string }[]) =>
					insps
						.map(insp => {
							const match = (inspectionTypes ?? []).find(
								t => t.inspectionTypeId === insp.inspectionType,
							);
							return match ? this.langService.getTranslation(match.langId) || match.name : "";
						})
						.filter(v => v)
						.join(", ")
						.trim();

				if (startInspections?.value && rule?.tripStart?.inspectionTypes) {
					startInspections.value = summarize(rule.tripStart.inspectionTypes);
				}

				if (endInspections?.value && rule?.tripEnd?.inspectionTypes) {
					endInspections.value = summarize(rule.tripEnd.inspectionTypes);
				}
			});
	}

	applyChangesOnZonesSelected(divisionId: string) {
		combineLatest([this.startAndEndZones$, this.zonesService.selectedZonesExcluded$])
			.pipe(takeUntil(this.onDestroy$))
			.subscribe(([startEndZones, exZones]) => {
				const zoneList = Array.isArray(exZones) ? exZones : [];
				const filteredExZones = zoneList.filter(zone => this._zoneMatchesDivision(zone, divisionId));
				this.setExcludedZones(filteredExZones);

				if (filteredExZones?.length > 1) {
					this.title = "Multiple zones selected";
				} else if (filteredExZones?.length === 1) {
					this.title = `${filteredExZones[0].name}`;
				} else {
					this.title = "Select zones to exclude";
				}
				this.setHasDupes(startEndZones, filteredExZones, this.rulesService.disableSaveButton$);
				this.updateSaveButtonState();
			});
	}

	setHasDupes(
		startEndZones: { start: RuleZone[]; end: RuleZone[] },
		exZones: DisplayedZone[],
		disableSaveButton$: BehaviorSubject<boolean>,
	) {
		this.hasDupeZones = false;

		if (!startEndZones) {
			return;
		}
		if (!startEndZones.start && !startEndZones.end.length) {
			return;
		}
		const { start, end } = startEndZones;
		if (exZones?.length) {
			const startIds = start?.length ? start.map(z => z.zoneId) : [];
			const endIds = end?.length ? end.map(z => z.zoneId) : [];

			const dutyZoneIds = [...new Set([...startIds, ...endIds])].reduce((acc, zoneId) => {
				acc[zoneId] = true;
				return acc;
			}, {});
			for (let i = 0; i < exZones.length; i++) {
				const zone = exZones[i].id;

				if (dutyZoneIds[zone]) {
					this.hasDupeZones = true;
					break;
				}
			}
		}

		disableSaveButton$.next(this.hasDupeZones);
	}

	closeDialog() {
		this.zonesService.closeDialog$
			.pipe(
				tap(_ => {
					this.dialog.closeAll();
				}),
				mergeMap(_ => {
					return combineLatest([
						this.startAndEndZones$.pipe(filter(isDefined)),
						this.zonesService.selectedZonesExcluded$,
					]);
				}),
				takeUntil(this.onDestroy$),
			)
			.subscribe(([startEndZones, exZones]) => {
				this.setHasDupes(startEndZones, exZones, this.rulesService.disableSaveButton$);
			});
	}

	ngOnInit(): void {
		this.catMap = this.zonesService.categoryMaps[EXCLUDED_ZONES].map;
		this.closeDialog();

		this.stepValidators = this.getStepValidators();
		const zoneStepLabels = new Set(["Duty Start", "Duty End"]);
		const isElementVisible = (el: { value?: unknown; error?: unknown; extraHtml?: unknown }) => {
			const hasValue = typeof el.value === "string" ? el?.value.trim().length > 0 : !!el?.value;
			const hasError = typeof el.error === "string" ? el?.error.trim().length > 0 : !!el?.error;
			const hasExtra = !!el.extraHtml;
			return hasValue || hasError || hasExtra;
		};

		let filteredElements;
		this.stepsWithElements = this.stepValidators
			?.map(stepValidator => {
				const rawElements =
					typeof stepValidator.getValidatedElements === "function"
						? stepValidator.getValidatedElements(this.wizardService)
						: [];

				filteredElements = (rawElements || []).filter(isElementVisible);

				return {
					stepLabel: stepValidator.stepLabel,
					validatedElements: filteredElements,
				};
			})
			.filter(step => step.validatedElements.length > 0 && zoneStepLabels.has(step.stepLabel));

		this.setInspectionsSummary(this.stepsWithElements);

		this.zonesService.divisionId$.pipe(takeUntil(this.onDestroy$)).subscribe(divId => {
			this.divisionId = divId;
			this.setZones(this.stepsWithElements, divId);
			this.applyChangesOnZonesSelected(divId);
		});

		const hasErrors =
			this.stepsWithElements.length === 0 ||
			this.stepsWithElements.some(step => step.validatedElements.some(el => el.error));

		if (hasErrors) {
			this.wizardService.disableSaveButton(this.wizardId);
		}

		this.selectedCompanyId$.pipe(filter(isDefined), takeUntil(this.onDestroy$)).subscribe(selectedCompanyId => {
			this.rulesService
				.getAllPages(selectedCompanyId)
				.pipe(takeUntil(this.onDestroy$))
				.subscribe(rules => {
					this.existingRuleNames = rules.map(rule => rule.name);

					// Re-evaluate duplicate state once we actually have names
					const currentName = (this.form?.get("ruleName")?.value ?? "").toString();
					this.showNameExistsError = this.nameExists(currentName);
					this.updateSaveButtonState();
				});
		});

		const ruleName = (this.wizardService.getValue(this.wizardId, StepProps.ReviewAndSubmit_RuleName) ||
			"") as string;

		this.form = this.fb.group({
			ruleName: [ruleName, [Validators.required, Validators.maxLength(25)]],
			d: [""],
		});

		// Initial duplicate check (may be false until names load)
		this.showNameExistsError = this.nameExists(ruleName);
		this.updateSaveButtonState();

		this.form
			.get("ruleName")
			?.valueChanges.pipe(takeUntil(this.onDestroy$))
			.subscribe((value: string) => {
				const trimmed = (value ?? "").toString().trim();
				this.showNameExistsError = this.nameExists(trimmed);
				this.wizardService.setValue(this.wizardId, StepProps.ReviewAndSubmit_RuleName, trimmed);
				this.updateSaveButtonState();
			});

		// listen for save button
		this.wizardService
			.listenToSaveButtonClicked(this.wizardId)
			.pipe(takeUntil(this.onDestroy$))
			.subscribe(() => {
				this.onSave();
			});
	}

	setZones(stepValidators: StepWithElements[] = this.stepsWithElements, divisionId) {
		const ZONE_LABEL = "Zones";
		const START_STEP_LABEL = "Duty Start";
		const END_STEP_LABEL = "Duty End";
		const stepsToUpdate = stepValidators ?? [];

		combineLatest([this.zonesService.selectedZonesStart$, this.zonesService.selectedZonesEnd$])
			.pipe(takeUntil(this.onDestroy$))
			.subscribe(([startZones, endZones]) => {
				const startList = Array.isArray(startZones) ? startZones : [];
				const endList = Array.isArray(endZones) ? endZones : [];
				const filteredStart = startList.filter(zone => this._zoneMatchesDivision(zone, divisionId));
				const filteredEnd = endList.filter(zone => this._zoneMatchesDivision(zone, divisionId));
				const mappedStart = filteredStart.map(z => ({ zoneId: z.id, categoryId: z.categoryId }));
				const mappedEnd = filteredEnd.map(z => ({ zoneId: z.id, categoryId: z.categoryId }));

				this._applyZonesToStep(
					stepsToUpdate,
					START_STEP_LABEL,
					ZONE_LABEL,
					this._normalizeZoneNames(filteredStart),
					TripSemantics.tripStart,
				);
				this._applyZonesToStep(
					stepsToUpdate,
					END_STEP_LABEL,
					ZONE_LABEL,
					this._normalizeZoneNames(filteredEnd),
					TripSemantics.tripEnd,
				);

				this.stepsWithElements = (stepsToUpdate ?? []).map(step => {
					return {
						stepLabel: step.stepLabel,
						validatedElements: [...step.validatedElements],
					};
				});

				this.startAndEndZones$.next({
					start: mappedStart,
					end: mappedEnd,
				});

				this.updateSaveButtonState();
			});
	}

	private _normalizeZoneNames(zones: DisplayedZone[] | null | undefined): string {
		return Array.isArray(zones)
			? zones
					.map(zone => (zone?.name ?? "").trim())
					.filter(name => name.length > 0)
					.join(", ")
			: "";
	}

	private _zoneMatchesDivision(zone: DisplayedZone | null | undefined, divisionId: string): boolean {
		if (!zone) {
			return false;
		}
		return zone.divisionId === divisionId || !zone.divisionId;
	}

	private _applyZonesToStep(
		steps: StepWithElements[],
		stepLabel: string,
		zoneLabel: string,
		zoneSummary: string,
		tripSemantics: TripSemantics,
	): void {
		const targetStep = steps.find(step => step.stepLabel === stepLabel);
		if (!targetStep) {
			return;
		}

		const withoutZones = targetStep.validatedElements.filter(el => el.label !== zoneLabel);
		const startCondition = this.wizardService.getValue(
			this.wizardId,
			makeKey(StepProps.DefineDuty_StartCondition, tripSemantics),
		) as string;

		if (startCondition !== "selected-zone") {
			targetStep.validatedElements = withoutZones;
			return;
		}

		const zoneElement = {
			label: zoneLabel,
			value: zoneSummary,
			error: zoneSummary ? null : "Required",
		};
		const inspectionLabel = "Inspection Types";
		const inspIndex = withoutZones.findIndex(el => el.label === inspectionLabel);
		const insertAt = inspIndex >= 0 ? inspIndex + 1 : withoutZones.length;
		targetStep.validatedElements = [
			...withoutZones.slice(0, insertAt),
			zoneElement,
			...withoutZones.slice(insertAt),
		];
	}

	openZoneSelector() {
		const dialogRef = this.dialog.open(ZonesSelectorComponent, {
			data: {
				tripType: this.EXCLUDED_ZONES,
				rule: this.rulesService.ruleToPatch$.value,
			},
			autoFocus: true,
			restoreFocus: true,
			disableClose: true,
			width: "1000px",
			height: "100vh",
		});

		// Restore focus to the zone selector button after the dialog closes
		dialogRef.afterClosed().subscribe(() => {
			const zoneBtn = document.getElementById("exclude-zone-button") as HTMLElement;
			if (zoneBtn) {
				zoneBtn.focus();
			}
		});
	}

	private nameExists(name: string): boolean {
		const _ruleId = this.wizardService.getValue(this.wizardId, StepProps.ReviewAndSubmit_RuleId) as string;
		const originalName = this.wizardService.getValue(WizardId.createRule, StepProps.Common_Original_RuleName);

		const candidate = (name ?? "").toString().trim().toLocaleLowerCase();
		if (!candidate) return false;

		// if we are in patch mode and the name is the same, don't show the warning and allow user to continue
		if (_ruleId && originalName === name) {
			return false;
		}
		return (
			this.existingRuleNames.map(n => n?.toString()?.toLocaleLowerCase() ?? "").includes(candidate) &&
			name !==
				((this.wizardService.getValue(WizardId.createRule, StepProps.Common_Original_RuleName) || "") as string)
					.trim()
					.toLocaleLowerCase()
		);
	}

	private updateSaveButtonState(): void {
		const dataChanged = this.isDataChanged();
		const nameCtrl = this.form?.get("ruleName");
		const invalidName =
			!nameCtrl || nameCtrl.invalid || nameCtrl.value.toString().trim().length === 0 || this.showNameExistsError;

		// Disable if: any step has errors OR rule name is invalid/duplicate OR rule data doesn't have any changes
		if (this.anyStepHasErrors() || invalidName || !this.stepsWithElements.length || !dataChanged) {
			this.wizardService.disableSaveButton(this.wizardId);
		} else {
			this.wizardService.enableSaveButton(this.wizardId);
		}
	}

	setExcludedZones(zones: DisplayedZone[]) {
		this.excludedZones = zones.map(z => {
			return {
				zoneId: z.id,
				categoryId: z.categoryId,
			};
		});
		this.wizardService.setValue(this.wizardId, StepProps.ReviewAndSubmit_ExcludedZones, this.excludedZones);
	}

	ngOnDestroy() {
		this.onDestroy$.next();
		this.onDestroy$.complete();
	}

	stepHasErrors(step: StepWithElements) {
		return step.validatedElements.some(el => !!el.error);
	}

	anyStepHasErrors() {
		return this.stepsWithElements.some(step => this.stepHasErrors(step));
	}

	get ruleName() {
		return this.form.get("ruleName");
	}

	updateTripBlocks() {
		const getPayloads = this.getPayloads();

		// Merge trip blocks built by previous steps
		for (const getPayload of getPayloads) {
			this.mergedTripBlocks = { ...this.mergedTripBlocks, ...getPayload(this.wizardService) };
		}
	}

	onSave() {
		this.updateTripBlocks();

		// Top-level fields
		const ruleName =
			(this.wizardService.getValue(this.wizardId, StepProps.ReviewAndSubmit_RuleName) as string) || "";

		// Derive timezone from either trip side (prefer start)
		const tzStart =
			(this.wizardService.getValue(
				this.wizardId,
				makeKey(StepProps.DefineDuty_Timezone, TripSemantics.tripStart),
			) as string) || "";
		const tzEnd =
			(this.wizardService.getValue(
				this.wizardId,
				makeKey(StepProps.DefineDuty_Timezone, TripSemantics.tripEnd),
			) as string) || "";

		// Company context saved in wizard
		const companyId = this.wizardService.getValue(this.wizardId, StepProps.Common_CompanyId) as string;

		let _ruleToPatch;
		// in case there are zones to add to the tripStart/tripEnd bodies
		combineLatest([this.startAndEndZones$, this.rulesService.ruleToPatch$.pipe(first())])
			.pipe(
				first(),
				mergeMap(([{ start, end }, ruleToPatch]) => {
					const body: MirRuleCreateRequest = {
						...this.mergedTripBlocks, // { tripStart?, tripEnd? }
						name: ruleName,
						excludeZones: this.excludedZones.length ? this.excludedZones : null,
						active: true,
						assets: [], // intentionally empty for now
						companyId,
					};

					if (start?.length && body.tripStart) {
						body.tripStart.zones = isDefined(start) ? start : [];
					}
					if (end?.length && body.tripEnd) {
						// body.tripEnd.zones = end;
						body.tripEnd.zones = isDefined(end) ? end : [];
					}

					if (ruleToPatch) {
						body.assets = ruleToPatch.assets || [];

						// define duty component will modify patched inspection types in edit mode, else if user just skips to review and submit, they should still get the rule's insp types to enable a successful edit
						if (ruleToPatch?.tripStart?.inspectionTypes && body?.tripStart?.inspectionTypes) {
							body.tripStart.inspectionTypes = ruleToPatch.tripStart.inspectionTypes;
						}
						if (ruleToPatch?.tripEnd?.inspectionTypes && body?.tripEnd?.inspectionTypes) {
							body.tripEnd.inspectionTypes = ruleToPatch.tripEnd.inspectionTypes;
						}
					}

					this.wizardService.disableSaveButton(this.wizardId);

					// setting a variable that can be accessed in the error block if API error
					_ruleToPatch = ruleToPatch;

					const req$ = ruleToPatch
						? this.rulesService.patchRule(ruleToPatch.id, body)
						: this.rulesService.createRule(body);
					return req$.pipe(first());
				}),
			)
			.subscribe({
				next: _res => {
					this.wizardService.enableSaveButton(this.wizardId);
					this.wizardService.saveCompleted(this.wizardId);
				},
				error: err => {
					console.error(_ruleToPatch ? "PUT /rules failed" : "POST /rules failed", err);
					this.wizardService.setHasError(this.wizardId);
					this.wizardService.enableSaveButton(this.wizardId);
				},
			});
	}

	isDataChanged(): boolean {
		const latestData = this.rulesService.ruleToPatch$.getValue();
		const initData = this.wizardService.getValue(this.wizardId, StepProps.Common_Original_Data) as MirRule;
		// ignore create new rule case
		if (!latestData && this.stepsWithElements) {
			return true;
		}

		if (isDefined(initData) && isDefined(latestData)) {
			// prepare data to compare
			this.updateTripBlocks();
			const ruleName = (this.wizardService.getValue(this.wizardId, StepProps.ReviewAndSubmit_RuleName) ||
				"") as string;
			const latestZones = this.startAndEndZones$.getValue();
			latestData.tripStart = this.mergedTripBlocks.tripStart;
			latestData.tripEnd = this.mergedTripBlocks.tripEnd;
			if (latestData.tripStart) {
				latestData.tripStart.zones = [...latestZones.start];
			}
			if (latestData.tripEnd) {
				latestData.tripEnd.zones = [...latestZones.end];
			}

			// compare rule name
			if (initData.name != ruleName) {
				return true;
			}
			// compare trip start
			if (this.compareTripDefine(initData.tripStart, latestData.tripStart)) {
				return true;
			}
			// compare trip end
			if (this.compareTripDefine(initData.tripEnd, latestData.tripEnd)) {
				return true;
			}
			// compare exclude zones
			const beforeZones = initData.excludedZones;
			const afterZones = this.excludedZones.length ? this.excludedZones : null;

			if (!beforeZones && !afterZones) return false;
			if (!beforeZones || !afterZones) return true;

			const beforeSet = new Set(beforeZones.map(z => z.zoneId));
			const afterSet = new Set(afterZones.map(z => z.zoneId));
			if (!this.areEqualSet(beforeSet, afterSet)) {
				return true;
			}
		}

		return false;
	}

	areEqualSet<T>(setA: Set<T>, setB: Set<T>): boolean {
		if (setA.size !== setB.size) {
			return false;
		}

		for (const item of setB) {
			if (!setA.has(item)) {
				return false;
			}
		}
		return true;
	}

	areEqualWorkPeriods(arr1: WorkPeriod[], arr2: WorkPeriod[]): boolean {
		if (arr1.length !== arr2.length) return false;

		const normalize = (wp: WorkPeriod) => ({
			days: [...wp.days].sort((a, b) => a - b).join(","),
			start: `${wp.start.hr}:${wp.start.min}`,
			end: `${wp.end.hr}:${wp.end.min}`,
		});

		const set1 = new Set(arr1.map(normalize).map(item => JSON.stringify(item)));
		const set2 = new Set(arr2.map(normalize).map(item => JSON.stringify(item)));

		if (set1.size !== set2.size) return false;

		for (const item of set1) {
			if (!set2.has(item)) return false;
		}
		return true;
	}

	compareTripDefine(before: TripStartOrEnd, after: TripStartOrEnd): boolean {
		if (!before && !after) return false;
		if (!!before !== !!after) {
			return true;
		}

		const hasChanged =
			before.definition !== after.definition ||
			before.frequency !== after.frequency ||
			before.within !== after.within ||
			!(before.timezone == after.timezone) ||
			!this.areEqualSet(
				new Set(before.inspectionTypes?.map(i => i.inspectionType) ?? []),
				new Set(after.inspectionTypes?.map(i => i.inspectionType) ?? []),
			) ||
			!this.areEqualSet(
				new Set(before.zones?.map(z => z.zoneId) ?? []),
				new Set(after.zones?.map(z => z.zoneId) ?? []),
			) ||
			!this.areEqualWorkPeriods(before.workPeriods ?? [], after.workPeriods ?? []);

		return hasChanged;
	}
}
